import React from 'react';

// Utility: Convert hex color to rgba with given alpha
function hexToRgba(hex, alpha) {
  let c = hex.replace('#', '');
  if (c.length === 3) c = c.split('').map(x => x + x).join('');
  const num = parseInt(c, 16);
  return `rgba(${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}, ${alpha})`;
}

/**
 * ProgressBar component (with preview and illegal state support).
 * Props: value (0-100), label, color, previewValue (0-100), illegal
 */
export default function ProgressBar({ value = 0, label = '', color = '#ff616d', previewValue, illegal }) {
  // Colors
  const previewColor = '#ECACB0'; // fixed preview color for all previews
  const illegalColor = '#ef4444';

  // Clamp values
  const mainValue = Math.max(0, Math.min(100, value));
  const preview = typeof previewValue === 'number' ? Math.max(0, Math.min(100, previewValue)) : null;

  // Border radius and height
  const barRadius = '9999px';
  const barHeight = '12px';

  // Diagonal stripes for illegal state
  const illegalStripes = illegal
    ? {
        background: `repeating-linear-gradient(135deg, ${illegalColor}, ${illegalColor} 10px, #fff3 10px, #fff3 20px)`,
        animation: 'stripe-move 1s linear infinite',
      }
    : {};

  // Main bar style
  const mainBarStyle = {
    width: `${illegal ? 100 : mainValue}%`,
    background: illegal ? illegalColor : color,
    opacity: 1,
    zIndex: 1,
    height: barHeight,
    position: 'absolute',
    left: 0,
    top: 0,
    borderTopLeftRadius: barRadius,
    borderBottomLeftRadius: barRadius,
    borderTopRightRadius: (!illegal && preview !== null && preview > mainValue) ? 0 : barRadius,
    borderBottomRightRadius: (!illegal && preview !== null && preview > mainValue) ? 0 : barRadius,
    transition: 'width 0.3s',
    ...illegalStripes,
  };

  // Preview bar logic (works for both positive and negative preview)
  let previewBarStyle = null;
  if (!illegal && preview !== null && preview !== mainValue) {
    const left = Math.min(mainValue, preview);
    const width = Math.abs(preview - mainValue);
    previewBarStyle = {
      left: `${left}%`,
      width: `${width}%`,
      background: previewColor,
      opacity: 1,
      zIndex: 3,
      height: barHeight,
      position: 'absolute',
      top: 0,
      borderTopLeftRadius: left === 0 ? barRadius : 0,
      borderBottomLeftRadius: left === 0 ? barRadius : 0,
      borderTopRightRadius: left + width === 100 ? barRadius : 0,
      borderBottomRightRadius: left + width === 100 ? barRadius : 0,
      transition: 'width 0.3s, left 0.3s',
    };
  }

  // Warning style for illegal state
  const containerClass = illegal ? 'border-2 border-red-500 animate-pulse' : '';

  return (
    <div className="w-full">
      <style>{`
        @keyframes stripe-move {
          0% { background-position: 0 0; }
          100% { background-position: 40px 0; }
        }
      `}</style>
      {label && <span className="block mb-1 text-sm text-gray-700">{label}</span>}
      <div className={`w-full bg-gray-200 rounded-full ${containerClass}`} style={{ height: barHeight, overflow: 'hidden', position: 'relative' }}>
        {/* Main bar (normal or illegal color) */}
        <div
          style={mainBarStyle}
          role="progressbar"
          aria-valuenow={mainValue}
          aria-valuemin={0}
          aria-valuemax={100}
        />
        {/* Preview extension (positive or negative) - always on top */}
        {previewBarStyle && <div style={previewBarStyle} />}
        {/* Container for stacking */}
        <div className="relative" style={{ height: barHeight }} />
      </div>
    </div>
  );
} 